import React, { useEffect, useState } from 'react';
import nomenclature from "assets/nomenclature.json";
import {
    Loader
} from "components";
import HTTP404 from "../views/HTTP404";

/**
 * Helper method to lookup the label
 * for a particular business type
 *
 * eg. In restaurant management 'Assets'
 * is termed as 'Equipments'
 *
 * @param label
 * @param businessType
 * @returns {*}
 */
export const getLabel = (label, businessType = 'physicalAssetManagement') => {
    return nomenclature[businessType][label];
};

/**
 * Helper method to format the date!
 * This takes care of all the notorious formats
 * Upkeep servers have used till date!
 *
 * @param date
 * @param locale
 * @param options
 * @returns {*}
 */
export const date = (
    date,
    locale = 'en-US',
    options = {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    }) => {
    if (typeof date === "object") {
        // expect a property named "iso"
        date = new Date(date.iso);
    } else {
        // handles both a js Date object
        // & raw iso string
        date = new Date(date);
    }

    return date.toLocaleDateString(locale, options);
};

export const getRoleDetails = (roleId) =>
    fetch(`${process.env.REACT_APP_API_URL}/api/app/client-settings`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ roleId })
    })
        .then(resp => resp.json())
        .then(resp => resp.results);


export const AppContext = React.createContext({});

/**
 * helper HOC to provide context required by the various components
 *  - role details
 *  - helper to get nomenclature based on role
 *
 * make sure this hoc is directly placed
 * at the top level views as it depends on match param
 * @param {Function} WrappedComponent
 * @return {Function}
 */
export const withAppContext = (WrappedComponent) => {
    return ({ match, ...props }) => {
        const [role, setRole] = useState({});
        const [loading, setLoading] = useState(true);

        if (!match || !match.params || !match.params.id) {
            // invalid content
            return <HTTP404/>;
        }

        useEffect(() => {
            // maintain loading state
            getRoleDetails(match.params.id)
                .then(setRole)
                .then(() => setLoading(false))
                .catch(() => setLoading(false));
        }, [match]);

        if (loading) {
            return <Loader/>;
        }

        if (!role || !role.objectId) {
            // invalid content
            return <HTTP404/>;
        }

        const { currencyDictionary } = role;

        const currencySymbol = currencyDictionary ?
            currencyDictionary.symbol : '$';

        const context = {
            role,
            currencySymbol,
            helpers: {
                n: (label) => getLabel(label, role.companyBusinessType),
                date,
            }
        };

        return <AppContext.Provider value={context}>
            <WrappedComponent
                {...props}
                match={match}
                role={role}
                roleId={role.objectId}
            />
        </AppContext.Provider>;
    };
};
